// src/components/promotion/ProductSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Modal, Input, Typography, Table, Pagination, Space, Spin, Empty } from "antd";
import type { ColumnsType } from "antd/es/table";
import { axiosInstance } from "src/api/axiosInstance";
import CustomButton from "src/components/common/CustomButton";

export type SelectedProductVariant = {
  variantId: number;
  variantCode: string;
  variantName: string;
  optionPrice: number;
};

export type SelectedProductAddon = {
  mappingId: number;
  mappingName: string;
  addonPrice: number;
};

export type VolumePrice = {
  minQuantity: number;
  discountType: "FIXED" | "PERCENTAGE";
  discountValue: number;
  isActive: boolean;
};

export type SelectedProduct = {
  id: number;
  name: string;
  code: string;
  productType: "SINGLE" | "OPTION";
  baseConsumerPrice: number;
  baseSupplyPrice: number;
  baseSalePrice: number;
  variants?: SelectedProductVariant[];
  addonMappings?: SelectedProductAddon[];
  volumePrices?: VolumePrice[];
};

type Props = {
  open: boolean;
  selectedIds: number[];
  selectedItems?: SelectedProduct[];
  onClose: () => void;
  onConfirm: (selectedItems: SelectedProduct[]) => void;
};

// 실제 API 응답 타입
interface Variant {
  variantId: number;
  variantCode: string;
  variantName: string;
  optionPrice: number;
  isSoldout: boolean;
  isActive: boolean;
  displayOrder: number;
}

interface AddonMapping {
  mappingId: number;
  mappingName: string;
  addonPrice: number;
  stockQuantity: number;
  isSoldout: boolean;
  isActive: boolean;
  displayOrder: number;
}

interface ProductApiItem {
  productId: number;
  productCode: string;
  productName: string;
  productType: "SINGLE" | "OPTION";
  baseConsumerPrice: number;
  baseSupplyPrice: number;
  baseSalePrice: number;
  variants?: Variant[] | null;
  addonMappings?: AddonMapping[] | null;
  volumePrices?: VolumePrice[] | null;
}

interface ProductListResponse {
  content: ProductApiItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

// 테이블에 표시할 행 타입 (product 단위)
interface ProductTableRow {
  productId: number;
  productCode: string;
  productName: string;
  productType: "SINGLE" | "OPTION";
  variantCount?: number; // 옵션 상품의 경우 variant 개수
}

// 임시 API 함수 (나중에 실제 API로 교체)
const getProductListApi = async (params: {
  productName?: string;
  page: number;
  size: number;
  order: string;
  include?: string;
}) => {
  // TODO: 실제 상품 API URL로 교체 필요
  const res = await axiosInstance.get<{ data: ProductListResponse }>(
    "/v1/admin/baseinfo/product/list", // 임시 엔드포인트
    {
      params,
      snackbar: { success: false },
    }
  );
  return res.data;
};

export default function ProductSelectModal({
  open,
  selectedIds,
  selectedItems,
  onClose,
  onConfirm,
}: Props) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(10);
  // productId 배열
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["product/list/modal", search, page, size] : null,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getProductListApi({
        productName: search.trim() || undefined,
        page: apiPage,
        size,
        order: "id,desc",
        include: "variants,addonMappings,volumePrices",
      });
    },
    { revalidateOnFocus: false }
  );

  const totalElements = data?.data?.page?.totalElements ?? 0;
  const totalPages = Math.max(1, data?.data?.page?.totalPages ?? 1);

  // product 단위로 테이블 행 변환
  const list: ProductTableRow[] = React.useMemo(() => {
    const apiContent = data?.data?.content ?? [];
    return apiContent.map((product) => ({
      productId: product.productId,
      productCode: product.productCode,
      productName: product.productName,
      productType: product.productType,
      variantCount: product.variants?.length || 0,
    }));
  }, [data]);

  const handleConfirm = () => {
    const apiContent = data?.data?.content ?? [];
    const result: SelectedProduct[] = tempSelected.map((productId) => {
      // 1. apiContent에서 찾기 (variants, addons, volumePrices, 가격 정보 포함)
      const apiProduct = apiContent.find((p) => p.productId === productId);
      if (apiProduct) {
        return {
          id: apiProduct.productId,
          name: apiProduct.productName,
          code: apiProduct.productCode,
          productType: apiProduct.productType,
          baseConsumerPrice: apiProduct.baseConsumerPrice || 0,
          baseSupplyPrice: apiProduct.baseSupplyPrice || 0,
          baseSalePrice: apiProduct.baseSalePrice || 0,
          variants:
            apiProduct.productType === "OPTION" && apiProduct.variants
              ? apiProduct.variants.map((v) => ({
                  variantId: v.variantId,
                  variantCode: v.variantCode,
                  variantName: v.variantName,
                  optionPrice: v.optionPrice,
                }))
              : undefined,
          addonMappings: apiProduct.addonMappings
            ? apiProduct.addonMappings.map((a) => ({
                mappingId: a.mappingId,
                mappingName: a.mappingName,
                addonPrice: a.addonPrice,
              }))
            : undefined,
          volumePrices: apiProduct.volumePrices || undefined,
        };
      }
      // 2. selectedItems에서 찾기 (다른 페이지)
      const prevSelected = selectedItems?.find((item) => item.id === productId);
      if (prevSelected) {
        return prevSelected;
      }
      // 3. 둘 다 없는 경우
      return {
        id: productId,
        name: `상품 ${productId}`,
        code: "",
        productType: "SINGLE",
        baseConsumerPrice: 0,
        baseSupplyPrice: 0,
        baseSalePrice: 0,
      };
    });
    onConfirm(result);
    onClose();
  };

  const columns: ColumnsType<ProductTableRow> = [
    { title: "상품ID", dataIndex: "productId", key: "productId", width: 80 },
    { title: "상품코드", dataIndex: "productCode", key: "productCode", width: 150 },
    { title: "상품명", dataIndex: "productName", key: "productName" },
    {
      title: "상품타입",
      dataIndex: "productType",
      key: "productType",
      width: 100,
      render: (type) => (type === "SINGLE" ? "단일" : "옵션"),
    },
    {
      title: "옵션수",
      dataIndex: "variantCount",
      key: "variantCount",
      width: 80,
      align: "center",
      render: (count, record) => (record.productType === "OPTION" ? count || 0 : "-"),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<Typography.Text strong>상품 선택</Typography.Text>}
      width={900}
      centered
      footer={[
        <CustomButton key="cancel" variant="outlined" onClick={onClose}>
          취소
        </CustomButton>,
        <CustomButton key="confirm" variant="contained" onClick={handleConfirm}>
          확인
        </CustomButton>,
      ]}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input
          placeholder="상품명 검색"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          onPressEnter={() => setPage(1)}
        />

        <Typography.Text type="secondary">선택된 상품: {tempSelected.length}개</Typography.Text>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <>
            <div style={{ border: "1px solid #e0e0e0", borderRadius: 8 }}>
              <Table
                rowKey="productId"
                columns={columns}
                dataSource={list}
                pagination={false}
                scroll={{ x: 1000 }}
                locale={{ emptyText: <Empty description="데이터가 없습니다." /> }}
                rowSelection={{
                  selectedRowKeys: tempSelected,
                  onChange: (keys) => setTempSelected(keys as number[]),
                  preserveSelectedRowKeys: true,
                }}
                onRow={(record) => ({
                  onClick: () => {
                    setTempSelected((prev) =>
                      prev.includes(record.productId)
                        ? prev.filter((id) => id !== record.productId)
                        : [...prev, record.productId]
                    );
                  },
                })}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
              <Pagination
                current={Math.min(page, totalPages)}
                total={totalElements}
                pageSize={size}
                showSizeChanger={false}
                onChange={(nextPage) => setPage(nextPage)}
              />
            </div>
          </>
        )}
      </Space>
    </Modal>
  );
}
